/**
 * Fixed tween presets. Each maps a normalized interpolation parameter `t` in [0,1] to an eased
 * parameter, which the evaluator then feeds to quaternion slerp. Pure, allocation-free.
 *
 * Easing metadata belongs to the transition *from* a keyframe *toward* the next one.
 */

import type { TweenPreset } from './types';

const HALF_PI = Math.PI / 2;

export function applyEasing(preset: TweenPreset, t: number): number {
  const x = t <= 0 ? 0 : t >= 1 ? 1 : t;
  switch (preset) {
    case 'linear':
      return x;
    case 'easeIn':
      return x * x;
    case 'easeOut': {
      const inv = 1 - x;
      return 1 - inv * inv;
    }
    case 'easeInOut':
      return x < 0.5 ? 2 * x * x : 1 - 2 * (1 - x) * (1 - x);
    case 'step':
      // Hold the previous keyframe until the next one is reached.
      return x < 1 ? 0 : 1;
    case 'sineIn':
      return 1 - Math.cos(x * HALF_PI);
    case 'sineOut':
      return Math.sin(x * HALF_PI);
    case 'sineInOut':
      return -(Math.cos(Math.PI * x) - 1) / 2;
    case 'cubicIn':
      return x * x * x;
    case 'cubicOut': {
      const inv = 1 - x;
      return 1 - inv * inv * inv;
    }
    case 'cubicInOut':
      return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
    default: {
      // Unknown presets degrade to linear rather than producing NaN.
      const exhaustive: never = preset;
      void exhaustive;
      return x;
    }
  }
}

/**
 * The preset that plays the same curve backwards: `mirror(f)(t) === 1 - f(1 - t)`.
 *
 * Used when a span of keyframes is reversed in time (`reverseKeyframes`). The in/out pairs swap;
 * `linear` and every `*InOut` are already symmetric and map to themselves.
 *
 * `step` is the one preset with NO mirror in this set — its reverse is "hold the RIGHT key",
 * which no preset expresses. It maps to itself, so a reversed `step` segment holds the wrong
 * side. Adding a `stepEnd` preset is not an option: persistence validation rejects easings
 * outside `TWEEN_PRESETS` (`src/persistence/validate.ts`), so old builds could not read the file.
 */
export function mirrorEasing(preset: TweenPreset): TweenPreset {
  switch (preset) {
    case 'easeIn':
      return 'easeOut';
    case 'easeOut':
      return 'easeIn';
    case 'sineIn':
      return 'sineOut';
    case 'sineOut':
      return 'sineIn';
    case 'cubicIn':
      return 'cubicOut';
    case 'cubicOut':
      return 'cubicIn';
    // linear, easeInOut, sineInOut, cubicInOut are symmetric; step has no mirror (see above).
    default:
      return preset;
  }
}
